/**
 * AI 配置管理 — API Key 加密存储
 *
 * 加密方案：Web Crypto API (AES-GCM)
 * - 密钥每次会话随机生成，存 sessionStorage（关闭浏览器即失效）
 * - API Key 加密后存 localStorage（即使被读取也是密文）
 * - 首次加载时如果没有密钥，生成新的并重新加密
 *
 * 注意：这不是严格的端到端加密（本地 JS 环境），但防止
 * localStorage 被简单明文读取。完整保护需 Tauri Keychain（v1.1）。
 */

export interface AiConfig {
  provider: 'openai' | 'deepseek' | 'custom';
  apiKey: string;
  model: string;
  baseUrl: string;
}

const DEFAULT_BASE_URLS: Record<string, string> = {
  openai: 'https://api.openai.com/v1',
  deepseek: 'https://api.deepseek.com/v1',
  custom: '',
};

const STORAGE_KEY = 'hump-ai-config';
const KEY_STORAGE = 'hump-crypto-key';

// ============================================================================
// 加密工具（XOR + Base64）
// 密钥存 localStorage（非 sessionStorage），跨刷新持久。
// 非 AES 级别安全，但防止直接 localStorage 读取明文。
// v1.1 用 Tauri Keychain 做系统级加密。
// ============================================================================

/** 获取或创建加密密钥（存 localStorage，跨刷新持久） */
function getEncryptionKey(): string {
  let key = localStorage.getItem(KEY_STORAGE);
  if (!key) {
    // 首次生成随机密钥
    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    key = btoa(String.fromCharCode(...arr));
    localStorage.setItem(KEY_STORAGE, key);
  }
  return key;
}

/** 加密 */
function encode(text: string): string {
  const key = getEncryptionKey();
  const keyBytes = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
  const bytes = new TextEncoder().encode(text);
  const result = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    result[i] = bytes[i] ^ keyBytes[i % keyBytes.length];
  }
  return btoa(String.fromCharCode(...result));
}

/** 解密 */
function decode(encoded: string): string {
  const key = getEncryptionKey();
  const keyBytes = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
  const bytes = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
  const result = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    result[i] = bytes[i] ^ keyBytes[i % keyBytes.length];
  }
  return new TextDecoder().decode(result);
}

// ============================================================================
// 配置存取
// ============================================================================

interface StoredConfig {
  provider: string;
  encodedKey: string;
  model: string;
  baseUrl: string;
}

export async function getAiConfig(): Promise<AiConfig | null> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredConfig;
    const apiKey = decode(stored.encodedKey);
    return {
      provider: stored.provider as AiConfig['provider'],
      apiKey,
      model: stored.model,
      baseUrl: stored.baseUrl,
    };
  } catch {
    return null;
  }
}

export async function saveAiConfig(config: AiConfig): Promise<void> {
  const encodedKey = encode(config.apiKey);
  const stored: StoredConfig = {
    provider: config.provider,
    encodedKey,
    model: config.model,
    baseUrl: config.baseUrl,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

export function clearAiConfig(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function getBaseUrl(config: AiConfig): string {
  return config.baseUrl || DEFAULT_BASE_URLS[config.provider] || '';
}

export const DEFAULT_MODELS: Record<string, string> = {
  openai: 'gpt-4o-mini',
  deepseek: 'deepseek-chat',
  custom: '',
};

/** 同步检查（不解密，只看是否有存储的配置） */
export function isConfigured(): boolean {
  return !!localStorage.getItem(STORAGE_KEY);
}

/** 异步检查 + 解密验证 Key 非空 */
export async function isConfiguredAsync(): Promise<boolean> {
  const config = await getAiConfig();
  return !!config && !!config.apiKey;
}
